/**
 * ScriptBrowserPanel
 *
 * Scripts sidebar panel showing Pinned, Recent, and Mounted Folder sections
 * with search filtering and keyboard navigation.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { FileCode2, Pin, Clock } from 'lucide-react';
import { toast } from '../../../components/ui/toaster';
import { useScriptBrowserStore } from '../store/scriptBrowserStore';
import { useMountedFolderIndexStore } from '../store/mountedFolderIndexStore';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { useExplorerContextHint } from '../../command-palette/hooks/useExplorerContextHint';
import { isQueryTab, isNotebookTab } from '../../workspace/core/types/workspaceTypes';
import { openScriptInTab } from '../services/scriptBrowserApi';
import { scanAllMountedFolders } from '../services/mountedFolderScanService';
import { ScriptSection } from './ScriptSection';
import { ScriptToolbar } from './ScriptToolbar';
import { FileNotFoundDialog } from './FileNotFoundDialog';
import { WorkspaceSelector } from './WorkspaceSelector';
import { MountedFolderTree } from './MountedFolderTree';
import { MountedScriptsSearchSection } from './MountedScriptsSearchSection';
import type { MountedScriptMatch } from './MountedScriptsSearchSection';
import type { ScriptEntry, MountedFolder } from '../types/scriptBrowserTypes';
import { getFileNameFromPath } from '../../workspace/query-editor/utils/queryFileUtils';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
/** Normalize file path for comparison. */
const normalizePath = (p: string): string => p.replace(/\\/g, '/').trim();

/** Stable empty arrays to avoid infinite re-render loops in Zustand selectors. */
const EMPTY_STRINGS: string[] = [];
const EMPTY_FOLDERS: MountedFolder[] = [];

export function ScriptBrowserPanel() {
  const scripts = useScriptBrowserStore((s) => s.scripts);
  const sectionCollapseState = useScriptBrowserStore((s) => s.sectionCollapseState);
  const setSectionCollapse = useScriptBrowserStore((s) => s.setSectionCollapse);
  const activeWorkspaceId = useScriptBrowserStore((s) => s.activeWorkspaceId);
  const pinnedIds = useScriptBrowserStore((s) => {
    if (s.activeWorkspaceId) {
      const ws = s.workspaces.find((w) => w.id === s.activeWorkspaceId);
      return ws?.pinnedScriptIds ?? EMPTY_STRINGS;
    }
    return s.defaultWorkspaceState.pinnedScriptIds;
  });
  const rawMountedFolders = useScriptBrowserStore((s): MountedFolder[] => {
    if (s.activeWorkspaceId) {
      const ws = s.workspaces.find((w) => w.id === s.activeWorkspaceId);
      return ws?.mountedFolders ?? EMPTY_FOLDERS;
    }
    return s.defaultWorkspaceState.mountedFolders;
  });

  // Blueprint-backed folders pin to the top so the user lands on the
  // synced-with-DB folders first, then their plain mounted folders.
  // Stable sort preserves user-controlled order within each group.
  const mountedFolders = useMemo(() => {
    const blueprints: MountedFolder[] = [];
    const normals: MountedFolder[] = [];
    for (const f of rawMountedFolders) {
      if (f.kind === 'blueprint') blueprints.push(f);
      else normals.push(f);
    }
    return blueprints.length === 0 ? rawMountedFolders : [...blueprints, ...normals];
  }, [rawMountedFolders]);

  const isScanning = useMountedFolderIndexStore(
    (s) => s.scanningFolderIds.length > 0
  );
  const mountedFolderIndex = useMountedFolderIndexStore((s) => s.index);

  // Active connection's id + display name — used to suppress a script
  // row's server sublabel when it just repeats the connection the user is
  // already on (PP-29). Reuses the same connection/database selection the
  // command palette's context hint is built from, so "active connection"
  // means the same thing across both surfaces. Comparison is by id
  // (display names are not unique across saved connections); the name is
  // still threaded through as a fallback for scripts saved before
  // `connectionId` was tracked.
  const { connectionId: activeConnectionId, connectionName: activeConnectionName } =
    useExplorerContextHint();

  // Scan mounted folders on mount and workspace change
  useEffect(() => {
    if (mountedFolders.length > 0) {
      scanAllMountedFolders(mountedFolders);
    }
  }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe only to the derived file-path list with shallow equality — keeps
  // this panel from re-rendering on every keystroke in the active query tab
  // (which rewrites `state.tabs` via updateTabContent).
  const openFilePaths = useWorkspaceStore(
    useShallow((s) =>
      s.tabs
        .flatMap((t) => {
          if (isQueryTab(t)) return [normalizePath(t.content.filePath ?? '')];
          if (isNotebookTab(t)) return [normalizePath(t.content.filePath ?? '')];
          return [];
        })
        .filter(Boolean)
    )
  );

  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);
  const pinnedScripts = useMemo(
    () => scripts.filter((s) => pinnedSet.has(s.id)),
    [scripts, pinnedSet]
  );
  const recentScripts = useMemo(
    () =>
      scripts
        .filter((s) => !pinnedSet.has(s.id))
        .sort((a, b) =>
          new Date(b.lastOpenedAt).getTime() - new Date(a.lastOpenedAt).getTime()
        ),
    [scripts, pinnedSet]
  );

  const togglePin = useScriptBrowserStore((s) => s.togglePin);
  const removeScript = useScriptBrowserStore((s) => s.removeScript);

  const [fileNotFoundScript, setFileNotFoundScript] = useState<ScriptEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedScriptId, setFocusedScriptId] = useState<string | null>(null);

  const filteredPinned = useMemo(() => {
    if (!searchQuery) return pinnedScripts;
    const q = searchQuery.toLowerCase();
    return pinnedScripts.filter((s) => s.fileName.toLowerCase().includes(q));
  }, [pinnedScripts, searchQuery]);

  const filteredRecent = useMemo(() => {
    if (!searchQuery) return recentScripts;
    const q = searchQuery.toLowerCase();
    return recentScripts.filter((s) => s.fileName.toLowerCase().includes(q));
  }, [recentScripts, searchQuery]);

  const mountedMatches: MountedScriptMatch[] = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];

    const results: MountedScriptMatch[] = [];

    for (const folder of mountedFolders) {
      const normalizedRoot = normalizePath(folder.directoryPath).replace(/\/+$/, '');
      const indexed = mountedFolderIndex[folder.id] ?? [];

      for (const file of indexed) {
        const fileName = getFileNameFromPath(file.path);
        if (!fileName.toLowerCase().includes(q)) continue;

        const normalizedFilePath = normalizePath(file.path);
        const relativePath =
          normalizedFilePath.startsWith(`${normalizedRoot}/`)
            ? normalizedFilePath.slice(normalizedRoot.length + 1)
            : undefined;

        results.push({
          filePath: file.path,
          fileName,
          description: relativePath ? `${folder.label} — ${relativePath}` : folder.label,
        });
      }
    }

    results.sort((a, b) =>
      a.fileName.localeCompare(b.fileName, undefined, { sensitivity: 'base' }) ||
      a.filePath.localeCompare(b.filePath, undefined, { sensitivity: 'base' })
    );

    return results;
  }, [mountedFolders, mountedFolderIndex, searchQuery]);

  const visibleScripts = useMemo(
    () => [...filteredPinned, ...filteredRecent],
    [filteredPinned, filteredRecent]
  );

  const handleOpen = useCallback(async (script: ScriptEntry) => {
    const result = await openScriptInTab(script.filePath);
    if (result === 'file-not-found') {
      setFileNotFoundScript(script);
    } else if (result === null) {
      toast.error({ title: 'Failed to open script', description: script.filePath });
    }
  }, []);

  const handleOpenMountedMatch = useCallback(async (filePath: string) => {
    const result = await openScriptInTab(filePath);
    if (result === 'file-not-found') {
      toast.error({ title: 'File not found', description: filePath });
    } else if (result === null) {
      toast.error({ title: 'Failed to open file', description: filePath });
    }
  }, []);

  const handleTogglePin = useCallback(
    (script: ScriptEntry) => { togglePin(script.id); },
    [togglePin]
  );

  const isActive = useCallback(
    (script: ScriptEntry) => openFilePaths.includes(normalizePath(script.filePath)),
    [openFilePaths]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      if (visibleScripts.length === 0) return;
      const idx = focusedScriptId
        ? visibleScripts.findIndex((s) => s.id === focusedScriptId)
        : -1;
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const next = idx < visibleScripts.length - 1 ? idx + 1 : 0;
          setFocusedScriptId(visibleScripts[next].id);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prev = idx > 0 ? idx - 1 : visibleScripts.length - 1;
          setFocusedScriptId(visibleScripts[prev].id);
          break;
        }
        case 'Enter':
          if (idx >= 0) { e.preventDefault(); handleOpen(visibleScripts[idx]); }
          break;
        case 'Delete':
          if (idx >= 0) {
            e.preventDefault();
            const script = visibleScripts[idx];
            removeScript(script.id);
            toast.default({
              title: `Removed ${script.fileName}`,
              action: { label: 'Undo', onClick: () => useScriptBrowserStore.getState().restoreScript(script) },
            });
            const nextIdx = idx < visibleScripts.length - 1 ? idx + 1 : idx - 1;
            setFocusedScriptId(nextIdx >= 0 ? visibleScripts[nextIdx].id : null);
          }
          break;
        case 'Escape':
          setFocusedScriptId(null);
          break;
      }
    },
    [visibleScripts, focusedScriptId, handleOpen, removeScript]
  );

  const fileNotFoundDialog = fileNotFoundScript && (
    <FileNotFoundDialog
      script={fileNotFoundScript}
      onRemove={() => { removeScript(fileNotFoundScript.id); setFileNotFoundScript(null); }}
      onKeep={() => setFileNotFoundScript(null)}
    />
  );

  const hasContent = scripts.length > 0 || mountedFolders.length > 0;

  if (!hasContent) {
    return (
      <div className="flex flex-col h-full">
        <WorkspaceSelector />
        <ScriptToolbar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        <div className="flex flex-col items-center justify-center flex-1 p-4 text-center gap-3">
          <FileCode2 className="h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            Open or save a .sql file to see it here
          </p>
        </div>
        {fileNotFoundDialog}
      </div>
    );
  }

  const isPinnedExpanded = sectionCollapseState.pinned !== true;
  const isRecentExpanded = sectionCollapseState.recent !== true;
  const isFiltering = Boolean(searchQuery.trim());
  const noResults =
    isFiltering &&
    filteredPinned.length + filteredRecent.length + mountedMatches.length === 0;

  return (
    <div
      className="flex flex-col h-full outline-none"
      data-testid="script-browser-panel"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <WorkspaceSelector />
      <ScriptToolbar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      {isScanning && (
        <div className="flex items-center gap-1.5 px-3 py-1 text-xs text-muted-foreground">
          <RandomDotmSpinner size={12} />
          Indexing mounted folders...
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto px-1">
        {noResults ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No matching scripts
          </p>
        ) : (
          <>
            {filteredPinned.length > 0 && (
              <ScriptSection
                icon={Pin} label="Pinned" count={filteredPinned.length}
                isExpanded={isPinnedExpanded}
                onToggle={() => setSectionCollapse('pinned', isPinnedExpanded)}
                scripts={filteredPinned} isPinned isActive={isActive}
                onOpen={handleOpen} onTogglePin={handleTogglePin}
                focusedScriptId={focusedScriptId} testId="pinned-section"
                activeConnectionId={activeConnectionId}
                activeConnectionName={activeConnectionName}
              />
            )}

            {(!isFiltering || filteredRecent.length > 0) && (
              <ScriptSection
                icon={Clock} label="Recent" count={filteredRecent.length}
                isExpanded={isRecentExpanded}
                onToggle={() => setSectionCollapse('recent', isRecentExpanded)}
                scripts={filteredRecent} isActive={isActive}
                onOpen={handleOpen} onTogglePin={handleTogglePin}
                focusedScriptId={focusedScriptId} testId="recent-section"
                activeConnectionId={activeConnectionId}
                activeConnectionName={activeConnectionName}
              />
            )}

            {isFiltering ? (
              <MountedScriptsSearchSection
                matches={mountedMatches}
                onOpen={handleOpenMountedMatch}
              />
            ) : (
              mountedFolders.map((folder) => (
                <MountedFolderTree key={folder.id} folder={folder} />
              ))
            )}
          </>
        )}
      </div>
      {fileNotFoundDialog}
    </div>
  );
}
